import {MarkdownViewer} from '@github-ui/markdown-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {Button, ProgressBar, RelativeTime, StateLabel} from '@primer/react'
import {clsx} from 'clsx'
import {useState} from 'react'

import {LABELS} from './constants/labels'
import {MilestoneDate} from './MilestoneDate'
import styles from './RepositoryMilestone.module.css'

type MilestoneDetailDataProps = {
  optimisticDescription?: string
  milestone: {
    closed: boolean
    dueOn: string | null
    updatedAt: string
    description: string | null
    descriptionHTML: string | null
    progressPercentage: number
  }
}

export function MilestoneDetailData({milestone: currentMilestone, optimisticDescription}: MilestoneDetailDataProps) {
  const [isShowMore, setIsShowMore] = useState(false)

  const hasDescription =
    optimisticDescription !== undefined
      ? optimisticDescription.length > 0
      : Boolean(currentMilestone.description && currentMilestone.descriptionHTML)
  const descriptionExceedsLimit = (optimisticDescription ?? currentMilestone.descriptionHTML ?? '').length > 600

  const showMoreLessClass = () => {
    if (descriptionExceedsLimit) {
      return isShowMore ? styles.expanded : styles.collapsed
    } else {
      return null
    }
  }

  return (
    <div
      className={`${styles.milestoneDetailsWrapper} ${isShowMore && descriptionExceedsLimit ? styles.expanded : ''}`}
    >
      <div className={styles.status} data-testid="milestone-status">
        <StateLabel
          className={styles.milestoneStatus}
          variant="small"
          status={currentMilestone.closed ? 'closed' : 'open'}
        >
          {currentMilestone.closed ? LABELS.milestoneClosed : LABELS.milestoneOpen}
        </StateLabel>
        <div className={styles.milestoneDataContainer}>
          <MilestoneDate milestone={{closed: currentMilestone.closed, dueOn: currentMilestone.dueOn ?? null}} />
          <span className={styles.middot}>{LABELS.separator}</span>
          <span>
            {currentMilestone.closed ? <>{LABELS.milestoneClosed}</> : <>{LABELS.milestoneLastUpdated}</>}
            <RelativeTime date={new Date(currentMilestone.updatedAt)} tense="past" />
          </span>
        </div>
      </div>
      {hasDescription ? (
        <div className={styles.milestoneDescription} id="milestone-description">
          {optimisticDescription !== undefined ? (
            <p className={clsx(styles.mdViewer, styles.optimisticDescription, showMoreLessClass())}>
              {optimisticDescription}
            </p>
          ) : currentMilestone.description && currentMilestone.descriptionHTML ? (
            <MarkdownViewer
              className={clsx(styles.mdViewer, showMoreLessClass())}
              markdownValue={currentMilestone.description}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to GraphQL fragment MilestoneDetail.descriptionHTML */
              verifiedHTML={currentMilestone.descriptionHTML as SafeHTMLString}
              onChange={() => {}}
            />
          ) : null}
          {descriptionExceedsLimit && (
            <div className={clsx(styles.showMoreButtonContainer, isShowMore ? styles.expanded : styles.collapsed)}>
              <Button
                size="small"
                variant="invisible"
                className={styles.button}
                onClick={() => {
                  setIsShowMore(!isShowMore)
                }}
                aria-expanded={isShowMore}
                aria-controls="milestone-description"
                trailingVisual={isShowMore ? ChevronUpIcon : ChevronDownIcon}
              >
                {isShowMore ? 'Show less' : 'Show more'}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      <div className={styles.progressSection}>
        <span>
          <span className={styles.progressPercentage}>{Math.floor(currentMilestone.progressPercentage)}%</span> complete
        </span>
        <ProgressBar progress={Math.floor(currentMilestone.progressPercentage)} aria-hidden="true" />
      </div>
    </div>
  )
}
